import { ButtonClickAnalyticsType, KmsTypePresenter } from "@kaltura/mediaspace-shared-types";
import { Suspense, useState } from "react";
import { Carousel, ErrorBoundary } from "@kaltura/mediaspace-shared-ui";
import { useCreateUserSessionsFetcher, useLoadUserSessions, UserSessionsFetcher } from "./UserSessionsHelper";
import { GroupUserGridItem, GroupUserGridItemProps, GroupUserGridItemSkeleton } from "./GroupUserGridItem";
import { Box } from "@kaltura/mediaspace-shared-styled";
import { EventPresenterModal } from "@kaltura/mediaspace-shared-event-platform-event-list";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";

export interface GroupCarouselContainerProps {
    users: KmsTypePresenter[];
    ariaLabelledById?: string;
}

export const GroupCarouselContainer = ({ users, ariaLabelledById }: GroupCarouselContainerProps) => {
    const fetchSessions = useCreateUserSessionsFetcher();

    return (
        <ErrorBoundary fallback={<GroupCarouselSkeleton />}>
            <Suspense fallback={<GroupCarouselSkeleton />}>
                <GroupCarousel
                    fetchUsers={() => users}
                    fetchSessions={fetchSessions}
                    ariaLabelledById={ariaLabelledById}
                />
            </Suspense>
        </ErrorBoundary>
    );
};

export interface GroupCarouselProps {
    fetchUsers: () => KmsTypePresenter[];
    fetchSessions: UserSessionsFetcher;
    fullScreenWidth?: boolean;
    className?: string;
    ariaLabelledById?: string;
}

export const GroupCarousel = ({
    fetchUsers,
    fetchSessions,
    fullScreenWidth = false,
    className,
    ariaLabelledById,
}: GroupCarouselProps) => {
    const users = fetchUsers();
    const sessionsMap = useLoadUserSessions(users, fetchSessions);

    const sendButtonAnalytics = useButtonAnalytics();

    /*
     * Note: the userForModal state must stay defined when closing the modal,
     * because there's an animation of closing the modal, and the content must be available during the animation.
     *
     * This is the reason for having a separate openModal state, instead of fully depending on userForModal.
     */
    const [openModal, setOpenModal] = useState(false);
    const [userForModal, setUserForModal] = useState<KmsTypePresenter | undefined>();

    if (users.length === 0) {
        return null;
    }

    return (
        <>
            <Carousel
                items={users}
                itemComponent={GroupUserCarouselItem}
                itemProps={(user: KmsTypePresenter) => ({
                    sessions: sessionsMap[user.id] ?? [],
                    onClick: () => {
                        setUserForModal(user);
                        setOpenModal(true);
                        sendButtonAnalytics(
                            "User modal box - Open modal from thumbnail",
                            ButtonClickAnalyticsType.OPEN
                        );
                    },
                    centerText: fullScreenWidth,
                })}
                currentCardsNumberInSlides={5}
                mdCardsNumberInSlides={4}
                xsCardsNumberInSlides={2}
                fullScreenWidth={fullScreenWidth}
                container={"Group of users component"}
                className={className}
                ariaLabelledById={ariaLabelledById}
            />

            {userForModal && (
                <EventPresenterModal open={openModal} onClose={() => setOpenModal(false)} user={userForModal} />
            )}
        </>
    );
};

export const GroupCarouselSkeleton = () => {
    return (
        <Carousel
            items={[{}, {}, {}, {}]}
            itemComponent={GroupUserCarouselItemSkeleton}
            currentCardsNumberInSlides={4}
        />
    );
};

export const GroupUserCarouselItem = (props: GroupUserGridItemProps) => (
    <Box>
        <GroupUserGridItem analyticsAction={"Open user modal box - promoted"} {...props} />
    </Box>
);

export const GroupUserCarouselItemSkeleton = () => (
    <Box>
        <GroupUserGridItemSkeleton />
    </Box>
);
